import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { guideReturnPath } from './guide-bot/authReturn';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import {
  auth,
  registerWithEmailAndPassword,
  signInWithGoogle,
} from "./firebase";
import "./css/Register.css";
import { isEarlyAccess, RELEASE_STATE_MESSAGES } from "./releaseState";
import { ROUTES } from "./routes";
import { requiresEmailVerification } from './authVerification';
function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [acceptedNotice, setAcceptedNotice] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [user, loading] = useAuthState(auth);
  const history = useNavigate();
  const location = useLocation();
  const guideReturn = guideReturnPath(location.search);

  const validateAcceptance = () => {
    if (!acceptedNotice) {
      setAcceptanceError("You must accept the Terms of Use, Privacy Policy, and Early Access notice before creating an account.");
      return false;
    }

    setAcceptanceError("");
    return true;
  };

  const validateEmail = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError("Enter your email address.");
      return false;
    }

    const input = document.createElement('input');
    input.type = 'email';
    input.value = trimmedEmail;
    if (!input.checkValidity()) {
      setEmailError("Enter a valid email address.");
      return false;
    }

    setEmailError("");
    return true;
  };

  const register = async () => {
    setFormError("");
    if (!name.trim()) {
      setFormError("Enter your full name.");
      return;
    }

    if (!validateEmail()) return;
    if (!validateAcceptance()) return;

    setSubmitting(true);
    try {
      const registeredUser = await registerWithEmailAndPassword(name.trim(), email.trim(), password);
      history(ROUTES.verifyEmail, {
        replace: true,
        state: { email: registeredUser.email || email.trim(), from: { pathname: guideReturn || ROUTES.mainView } },
      });
    } catch (error) {
      console.error(error);
      setFormError(error?.code === 'auth/email-already-in-use'
        ? 'JourneyBot could not create that account. Try signing in or use a different email address.'
        : error?.message || 'JourneyBot could not create that account. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const registerWithGoogle = async () => {
    if (!validateAcceptance()) return;

    await signInWithGoogle({ recordLegalAcceptance: true, name });
  };

  useEffect(() => {
    if (loading) return;
    if (user) {
      history(requiresEmailVerification(user) ? ROUTES.verifyEmail : guideReturn || ROUTES.login,
        guideReturn ? { state: { from: { pathname: guideReturn } } } : undefined);
    }
  }, [user, loading, history, guideReturn]);
  return (
    <div className="register" data-testid="register-page">
      <div className="register__container">
        <TextField
          type="text"
          className="register__textBox"
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Full Name"
          autoComplete="name"
          required
          fullWidth
          inputProps={{ 'data-testid': 'register-name' }}
        />
        <TextField
          type="email"
          className="register__textBox"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
          }}
          onBlur={validateEmail}
          label="E-mail Address"
          autoComplete="email"
          required
          fullWidth
          error={Boolean(emailError)}
          helperText={emailError}
          inputProps={{ 'data-testid': 'register-email' }}
        />
        <TextField
          type="password"
          className="register__textBox"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          label="Password"
          autoComplete="new-password"
          required
          fullWidth
          inputProps={{ 'data-testid': 'register-password' }}
        />
        {formError ? <Alert severity="error" data-testid="register-form-error">{formError}</Alert> : null}
        {isEarlyAccess() ? (
          <p className="register__notice" data-testid="register-early-access-notice">
            {RELEASE_STATE_MESSAGES.globalBanner}
          </p>
        ) : null}
        <p className="register__agreement" data-testid="register-agreement">
          <label className="register__checkboxLabel">
            <input
              type="checkbox"
              checked={acceptedNotice}
              onChange={(event) => {
                setAcceptedNotice(event.target.checked);
                if (event.target.checked) setAcceptanceError("");
              }}
              aria-describedby={acceptanceError ? 'register-acceptance-error' : undefined}
              data-testid="register-acceptance-checkbox"
            />
            <span>
              I agree to the <Link to={ROUTES.terms}>Terms of Use</Link> and acknowledge the{' '}
              <Link to={ROUTES.privacy}>Privacy Policy</Link> and Early Access notice.
            </span>
          </label>
        </p>
        {acceptanceError ? (
          <p id="register-acceptance-error" className="register__error" role="alert" data-testid="register-acceptance-error">
            {acceptanceError}
          </p>
        ) : null}
        <Button className="register__btn" variant="contained" onClick={register} disabled={submitting} data-testid="register-submit">
          {submitting ? 'Creating account...' : isEarlyAccess() ? 'Create Early Access account' : 'Register'}
        </Button>
        <Button
          className="register__btn register__google"
          onClick={registerWithGoogle}
          disabled={submitting}
          variant="outlined"
        >
          Register with Google
        </Button>
        <div>
          Already have an account? <Link to={guideReturn ? `${ROUTES.login}?returnTo=%2Fguide-the-bot` : ROUTES.login}>Login</Link> now.
        </div>
      </div>
    </div>
  );
}
export default Register;
